/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import saveAs from 'file-saver';
import { toast } from 'react-hot-toast';
import { useLocalStorage } from 'react-use';
import {
  CustomPaging,
  DataTypeProvider,
  EditingState,
  IntegratedSelection,
  PagingState,
  SearchState,
  SelectionState,
  SortingState,
  IntegratedSorting
} from '@devexpress/dx-react-grid';
import { GridExporter } from '@devexpress/dx-react-grid-export';
import {
  ColumnChooser,
  ExportPanel,
  Grid,
  PagingPanel,
  SearchPanel,
  Table,
  TableColumnResizing,
  DragDropProvider,
  TableColumnVisibility,
  TableEditColumn,
  TableHeaderRow,
  TableInlineCellEditing,
  TableSelection,
  Toolbar,
  TableColumnReordering
} from '@devexpress/dx-react-grid-material-ui';
import { Alert, Box, CircularProgress, Paper, Typography } from '@mui/material';
import invoiceService from '../../../services/InvoiceService';
import purchaseService from '../../../services/PurchaseService';
import ImageFullScreenDialog from '../../../shared/dialog/ImageFullScreenDialog';
import AutoSaveInlineCell from '../../common/AutoSaveInlineCell';
import ResponsiveTableContainer, { StickyTableHead } from '../../common/ResponsiveTableContainer';
import HeaderFilterCell, { HeaderFilterContext } from '../../Invoices/table/customTemplate/HeaderFilterCell';
import CustomToolbarRoot from '../../UserNew/table/customTemplate/CustomToolbarRoot';
import KebabCommandCell from './KebabCommandCell';
import PurchasePopup from './PurchasePopup';
import { BoxshallProductProvider, DateProvider, NumberProvider, ReferenceProvider } from './PurchaseTableEditors';

const COLUMNS = [
  { name: 'boxshallName', title: 'Boxshall Name' },
  { name: 'boxshallDescription', title: 'Boxshall Description' },
  { name: 'invoiceDate', title: 'Invoice Date' },
  { name: 'invoiceNumber', title: 'Invoice No.' },
  { name: 'supplierName', title: 'Supplier DisplayName' },
  { name: 'supplierCode', title: 'Supplier Code' },
  { name: 'supplierDescription', title: 'Supplier Description' },
  { name: 'qty', title: 'Qty' },
  { name: 'unitPrice', title: 'Unit / List Price (AUD)' },
  { name: 'category', title: 'Category' },
  { name: 'subCategory', title: 'Subcategory' },
  { name: 'customerRef', title: 'Customer Ref' },
  { name: 'class', title: 'Class' },
  { name: 'invoiceFile', title: 'Invoice PDF' }
];

const PURCHASE_FIELDS = [
  'boxshallName',
  'boxshallDescription',
  'invoiceDate',
  'invoiceNumber',
  'supplierName',
  'supplierCode',
  'supplierDescription',
  'qty',
  'unitPrice',
  'category',
  'subCategory',
  'class'
];
// eslint-disable-next-line no-undef
const IMMEDIATE_COMMIT_COLUMNS = new Set(['boxshallName', 'supplierName', 'category', 'subCategory', 'class']);
const PurchaseAutoSaveCell = (props) => (
  <AutoSaveInlineCell {...props} immediateCommitColumns={IMMEDIATE_COMMIT_COLUMNS} />
);
// eslint-disable-next-line no-undef
const REFERENCE_FIELDS = new Set(['boxshallName', 'supplierName', 'category', 'subCategory', 'customerRef', 'class']);
const EXPORT_COLUMNS = COLUMNS.map((column) => ({
  ...column,
  getCellValue: (row) => {
    const value = row[column.name];
    if (REFERENCE_FIELDS.has(column.name)) return value?.name || '';
    if (column.name === 'invoiceDate' && value) return String(value).slice(0, 10);
    if (column.name === 'invoiceFile') return value?.webUrl || value?.path || '';
    return value ?? '';
  }
}));

const DEFAULT_WIDTHS = [
  { columnName: 'boxshallName', width: 180 },
  { columnName: 'boxshallDescription', width: 250 },
  { columnName: 'invoiceDate', width: 135 },
  { columnName: 'invoiceNumber', width: 140 },
  { columnName: 'supplierName', width: 210 },
  { columnName: 'supplierCode', width: 135 },
  { columnName: 'supplierDescription', width: 250 },
  { columnName: 'qty', width: 95 },
  { columnName: 'unitPrice', width: 170 },
  { columnName: 'category', width: 160 },
  { columnName: 'subCategory', width: 175 },
  { columnName: 'customerRef', width: 210 },
  { columnName: 'class', width: 220 },
  { columnName: 'invoiceFile', width: 150 }
];

const DEFAULT_ORDER = [
  'boxshallName',
  'boxshallDescription',
  'invoiceDate',
  'invoiceNumber',
  'supplierName',
  'supplierCode',
  'supplierDescription',
  'qty',
  'unitPrice',
  'category',
  'subCategory',
  'customerRef',
  'class',
  'invoiceFile'
];

const EMPTY_OPTIONS = {
  suppliers: [],
  categories: [],
  subcategories: [],
  classes: []
};

const toGridRow = (purchase) => ({
  id: String(purchase._id),
  boxshallName: purchase.item,
  boxshallDescription: purchase.boxshallDescription || '',
  invoiceDate: purchase.invoiceDate,
  invoiceNumber: purchase.invoiceNumber || '',
  supplierName: purchase.supplier,
  supplierCode: purchase.supplierCode || '',
  supplierDescription: purchase.supplierDescription || '',
  qty: purchase.quantity,
  unitPrice: purchase.unitPrice?.amount,
  category: purchase.category,
  subCategory: purchase.subcategory,
  customerRef: purchase.customerRef,
  class: purchase.classRef,
  invoiceFile: purchase.source?.storedFile
    ? {
        ...purchase.source.storedFile,
        invoiceId: purchase.source.invoiceId ? String(purchase.source.invoiceId) : null
      }
    : null
});

const InvoiceFileFormatter = ({ value }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);

  useEffect(
    () => () => {
      // eslint-disable-next-line no-undef
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    },
    [pdfUrl]
  );

  if (!value?.invoiceId) {
    return <ImageFullScreenDialog value={value?.webUrl || null} />;
  }

  const closePdf = (event) => {
    event?.stopPropagation();
    setOpen(false);
  };

  const openPdf = async (event) => {
    event.stopPropagation();
    setOpen(true);
    if (pdfUrl || loading) return;
    setLoading(true);
    try {
      const response = await invoiceService.downloadInvoicePdf(value.invoiceId);
      const blob =
        // eslint-disable-next-line no-undef
        response.data instanceof Blob
          ? response.data
          : // eslint-disable-next-line no-undef
            new Blob([response.data], { type: 'application/pdf' });
      // eslint-disable-next-line no-undef
      setPdfUrl(URL.createObjectURL(blob));
    } catch (error) {
      setOpen(false);
      toast.error(errorMessage(error, 'Could not open the invoice PDF'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={openPdf}
        title={value.name || 'Open invoice PDF'}
        aria-label={value.name || 'Open invoice PDF'}
        style={{
          width: 30,
          height: 30,
          border: '1px solid #f5c6c6',
          borderRadius: 3,
          background: '#fef0f0',
          cursor: 'zoom-in',
          fontSize: 18,
          padding: 0
        }}
      >
        📄
      </button>
      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={value.name || 'Invoice PDF'}
          onClick={closePdf}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            background: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <button
            type="button"
            onClick={closePdf}
            aria-label="Close invoice PDF"
            style={{
              position: 'absolute',
              top: 16,
              right: 24,
              zIndex: 1,
              border: 0,
              background: 'transparent',
              color: '#fff',
              cursor: 'pointer',
              fontSize: 32
            }}
          >
            ✕
          </button>
          {loading ? (
            <CircularProgress sx={{ color: '#fff' }} />
          ) : (
            <iframe
              src={pdfUrl}
              title={value.name || 'Invoice PDF'}
              onClick={(event) => event.stopPropagation()}
              style={{ width: '100vw', height: '100vh', border: 0, background: '#fff' }}
            />
          )}
        </div>
      )}
    </>
  );
};

const EDITING_COLUMN_EXTENSIONS = COLUMNS.map(({ name }) => ({
  columnName: name,
  editingEnabled: PURCHASE_FIELDS.includes(name)
}));
const SORTING_STATE_COLUMN_EXTENSIONS = [{ columnName: 'invoiceFile', sortingEnabled: false }];

const errorMessage = (error, fallback) => error?.response?.data?.message || error?.message || fallback;

const withProductDefaults = (values) => ({
  ...values,
  boxshallDescription: values.boxshallDescription || values.boxshallName?.description,
  unitPrice:
    values.unitPrice === undefined || values.unitPrice === '' ? values.boxshallName?.unitPrice : values.unitPrice,
  category: values.category || values.boxshallName?.category,
  subCategory: values.subCategory || values.boxshallName?.subcategory
});

const purchasePayload = (row) => PURCHASE_FIELDS.reduce((payload, field) => ({ ...payload, [field]: row[field] }), {});

const validateValues = (values, requireEveryField = false) => {
  const requiredText = ['boxshallDescription', 'invoiceNumber', 'supplierCode'];
  if (
    requiredText.some(
      (field) =>
        (requireEveryField || Object.prototype.hasOwnProperty.call(values, field)) &&
        !String(values[field] || '').trim()
    )
  ) {
    return 'Boxshall Description, Invoice No. and Supplier Code are required.';
  }
  const requiredReferences = ['boxshallName', 'supplierName'];
  if (
    requiredReferences.some(
      (field) => (requireEveryField || Object.prototype.hasOwnProperty.call(values, field)) && !values[field]
    )
  ) {
    return 'Boxshall Name and Supplier are required.';
  }
  if ((requireEveryField || Object.prototype.hasOwnProperty.call(values, 'invoiceDate')) && !values.invoiceDate) {
    return 'Invoice Date is required.';
  }
  if (
    (requireEveryField || Object.prototype.hasOwnProperty.call(values, 'unitPrice')) &&
    !Number.isFinite(Number(values.unitPrice))
  ) {
    return 'Unit Price must be numeric.';
  }
  if (
    Object.prototype.hasOwnProperty.call(values, 'qty') &&
    values.qty !== '' &&
    values.qty !== null &&
    !Number.isFinite(Number(values.qty))
  ) {
    return 'Qty must be numeric or blank.';
  }
  return null;
};

const ClickablePurchaseRow = ({ row, onOpen, ...restProps }) => {
  const handleDoubleClick = (event) => {
    if (
      !row?.id ||
      event.target.closest('button, a, input[type="checkbox"], [role="checkbox"], [role="button"], [role="menuitem"]')
    ) {
      return;
    }
    onOpen(row);
  };

  return (
    <Table.Row
      {...restProps}
      row={row}
      onDoubleClick={handleDoubleClick}
      aria-label={row?.id ? `Open ${row.boxshallName?.name || 'purchase'} details` : undefined}
      style={{ ...restProps.style, cursor: row?.id ? 'pointer' : restProps.style?.cursor }}
    />
  );
};

const saveWorkbook = (workbook) => {
  workbook.xlsx.writeBuffer().then((buffer) => {
    // eslint-disable-next-line no-undef
    saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'Purchases.xlsx');
  });
};

const SORTING_COLUMN_EXTENSIONS = [
  {
    columnName: 'boxshallName',
    compare: (left, right) => String(left?.name || '').localeCompare(String(right?.name || ''))
  }
];

const PurchaseGrid = ({ headerFilterContextValue, ...gridProps }) => (
  <HeaderFilterContext.Provider value={headerFilterContextValue}>
    <Grid {...gridProps} />
  </HeaderFilterContext.Provider>
);

const PurchaseTable = () => {
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useLocalStorage('purchaseRowsPerPage', 50);
  const [sorting, setSorting] = useState([{ columnName: 'invoiceDate', direction: 'asc' }]);
  const [columnWidths, setColumnWidths] = useLocalStorage('purchaseColumnWidthsV1', DEFAULT_WIDTHS);
  const [itemColumnOrder, setItemColumnOrder] = useState(DEFAULT_ORDER);
  const [storedHiddenColumnNames, setHiddenColumnNames] = useLocalStorage('purchaseHiddenColumnsV1', []);
  const [columnFilters, setColumnFilters] = useLocalStorage('purchaseColumnFiltersV1', {});
  const [selection, setSelection] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState('');
  const [editingCells, setEditingCells] = useState([]);
  const [addedRows, setAddedRows] = useState([{}]);
  const [popupRow, setPopupRow] = useState(null);
  const [popupSaving, setPopupSaving] = useState(false);
  const [options, setOptions] = useState(EMPTY_OPTIONS);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [refreshVersion, setRefreshVersion] = useState(0);
  const exporterRef = useRef(null);

  const completeColumnWidths = useMemo(() => {
    // eslint-disable-next-line no-undef
    const savedWidths = new Map(
      (Array.isArray(columnWidths) ? columnWidths : []).map((width) => [width.columnName, width])
    );
    return DEFAULT_WIDTHS.map((defaultWidth) => savedWidths.get(defaultWidth.columnName) || defaultWidth);
  }, [columnWidths]);

  const completeColumnOrder = useMemo(() => {
    const savedOrder = (Array.isArray(itemColumnOrder) ? itemColumnOrder : []).filter(
      (name, index, names) => DEFAULT_ORDER.includes(name) && names.indexOf(name) === index
    );
    const completedOrder = [...savedOrder];
    DEFAULT_ORDER.forEach((name, defaultIndex) => {
      if (completedOrder.includes(name)) return;
      const precedingColumn = [...DEFAULT_ORDER.slice(0, defaultIndex)]
        .reverse()
        .find((columnName) => completedOrder.includes(columnName));
      const insertionIndex = precedingColumn ? completedOrder.indexOf(precedingColumn) + 1 : 0;
      completedOrder.splice(insertionIndex, 0, name);
    });
    return completedOrder;
  }, [itemColumnOrder]);

  const hiddenColumnNames = useMemo(() => {
    const validNames = COLUMNS.map(({ name }) => name);
    return (Array.isArray(storedHiddenColumnNames) ? storedHiddenColumnNames : []).filter(
      (name, index, names) => validNames.includes(name) && names.indexOf(name) === index
    );
  }, [storedHiddenColumnNames]);

  const safeColumnFilters = useMemo(
    () => (columnFilters && typeof columnFilters === 'object' && !Array.isArray(columnFilters) ? columnFilters : {}),
    [columnFilters]
  );

  const filterQuery = useMemo(
    () => (Object.keys(safeColumnFilters).length ? JSON.stringify(safeColumnFilters) : undefined),
    [safeColumnFilters]
  );

  const loadColumnFilterOptions = useCallback(
    async (columnName, search) => {
      const response = await purchaseService.getPurchaseFilterOptions({
        column: columnName,
        search: search || undefined,
        tableSearch: submittedSearch || undefined,
        filters: filterQuery,
        limit: 200
      });
      return response.data?.data || { values: [], total: 0 };
    },
    [filterQuery, submittedSearch]
  );

  const changeColumnFilters = useCallback(
    (nextFilters) => {
      setColumnFilters(nextFilters);
      setCurrentPage(0);
      setSelection([]);
    },
    [setColumnFilters]
  );

  const headerFilterContextValue = useMemo(
    () => ({
      rows,
      columnFilters: safeColumnFilters,
      onFilterChange: changeColumnFilters,
      loadFilterOptions: loadColumnFilterOptions
    }),
    [changeColumnFilters, loadColumnFilterOptions, rows, safeColumnFilters]
  );

  useEffect(() => {
    // eslint-disable-next-line no-undef
    const availableRowIds = new Set(rows.map(({ id }) => id));
    setSelection((current) => current.filter((rowId) => availableRowIds.has(rowId)));
  }, [rows]);

  useEffect(() => {
    let active = true;
    purchaseService
      .getPurchaseOptions()
      .then((response) => {
        if (active) setOptions({ ...EMPTY_OPTIONS, ...(response.data?.data || {}) });
      })
      .catch((error) => {
        if (active) toast.error(errorMessage(error, 'Could not load purchase options'));
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    // eslint-disable-next-line no-undef
    const timeout = window.setTimeout(() => {
      setCurrentPage(0);
      setSelection([]);
      setSubmittedSearch(searchValue.trim());
    }, 350);
    // eslint-disable-next-line no-undef
    return () => window.clearTimeout(timeout);
  }, [searchValue]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError('');
    purchaseService
      .getPurchases({
        page: currentPage + 1,
        limit: pageSize,
        sort: sorting[0]?.columnName,
        direction: sorting[0]?.direction,
        search: submittedSearch || undefined,
        filters: filterQuery
      })
      .then((response) => {
        if (!active) return;
        setRows((response.data?.data || []).map(toGridRow));
        setTotalCount(response.data?.pagination?.total || 0);
      })
      .catch((error) => {
        if (!active) return;
        setLoadError(errorMessage(error, 'Could not load purchases'));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [currentPage, filterQuery, pageSize, refreshVersion, sorting, submittedSearch]);

  const loadProductOptions = useCallback(async (search) => {
    try {
      const response = await purchaseService.searchBoxshallProducts(search);
      return (response.data?.data || []).map((product) => ({
        value: product.id,
        label: product.name,
        reference: product
      }));
    } catch (error) {
      toast.error(errorMessage(error, 'Could not search Boxshall Products'));
      return [];
    }
  }, []);

  const commitChanges = useCallback(
    async ({ added, changed, deleted }) => {
      if (added?.length) {
        const purchase = withProductDefaults(added[0]);
        const validationError = validateValues(purchase, true);
        if (validationError) {
          toast.error(validationError);
          return;
        }
        try {
          await purchaseService.createPurchase(purchase);
          toast.success('Purchase added to Verd');
          setAddedRows([{}]);
          setCurrentPage(0);
          setRefreshVersion((value) => value + 1);
        } catch (error) {
          toast.error(errorMessage(error, 'Could not add purchase'));
        }
      }

      if (changed) {
        const updates = Object.entries(changed);
        const validationError = updates.map(([, rowChanges]) => validateValues(rowChanges)).find(Boolean);
        if (validationError) {
          toast.error(validationError);
          return;
        }
        try {
          // eslint-disable-next-line no-undef
          const updatedRows = await Promise.all(
            updates.map(async ([rowId, rowChanges]) => {
              const response = await purchaseService.updatePurchase(rowId, rowChanges);
              return toGridRow(response.data.data);
            })
          );
          setRows((currentRows) => {
            // eslint-disable-next-line no-undef
            const updatedById = new Map(updatedRows.map((row) => [row.id, row]));
            return currentRows.map((row) => updatedById.get(row.id) || row);
          });
          toast.success(updates.length === 1 ? 'Purchase updated in Verd' : 'Purchases updated in Verd');
        } catch (error) {
          toast.error(errorMessage(error, 'Could not update purchase'));
          setRefreshVersion((value) => value + 1);
        }
      }

      if (deleted?.length) {
        // eslint-disable-next-line no-undef
        if (!window.confirm('Are you sure you want to delete this purchase from Verd?')) return;
        try {
          // eslint-disable-next-line no-undef
          await Promise.all(deleted.map((purchaseId) => purchaseService.deletePurchase(purchaseId)));
          setSelection((current) => current.filter((rowId) => !deleted.includes(rowId)));
          toast.success(deleted.length === 1 ? 'Purchase deleted from Verd' : 'Purchases deleted from Verd');
          const nextTotal = Math.max(0, totalCount - deleted.length);
          const lastPage = Math.max(0, Math.ceil(nextTotal / pageSize) - 1);
          if (currentPage > lastPage) setCurrentPage(lastPage);
          else setRefreshVersion((value) => value + 1);
        } catch (error) {
          toast.error(errorMessage(error, 'Could not delete purchase'));
          setRefreshVersion((value) => value + 1);
        }
      }
    },
    [currentPage, pageSize, totalCount]
  );

  const savePopup = useCallback(async (draft) => {
    const payload = purchasePayload(withProductDefaults(draft));
    const validationError = validateValues(payload, true);
    if (validationError) {
      toast.error(validationError);
      return;
    }
    setPopupSaving(true);
    try {
      const response = await purchaseService.updatePurchase(draft.id, payload);
      const updatedRow = toGridRow(response.data.data);
      setRows((current) => current.map((row) => (row.id === updatedRow.id ? updatedRow : row)));
      setPopupRow(null);
      toast.success('Purchase updated in Verd');
    } catch (error) {
      toast.error(errorMessage(error, 'Could not update purchase'));
    } finally {
      setPopupSaving(false);
    }
  }, []);

  const startExport = useCallback((exportOptions) => {
    exporterRef.current?.exportGrid(exportOptions);
  }, []);

  return (
    <Box>
      <Typography sx={{ mb: 1 }}>Total rows selected: {selection.length}</Typography>
      <Paper sx={{ position: 'relative', overflow: 'hidden' }}>
        {loadError && <Alert severity="error">{loadError}</Alert>}
        {loading && (
          <Box
            sx={{
              alignItems: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.65)',
              display: 'flex',
              inset: 0,
              justifyContent: 'center',
              minHeight: 120,
              position: rows.length ? 'absolute' : 'relative',
              zIndex: 5
            }}
          >
            <CircularProgress size={30} />
          </Box>
        )}
        <PurchaseGrid
          headerFilterContextValue={headerFilterContextValue}
          rows={rows}
          columns={COLUMNS}
          getRowId={(row) => row.id}
        >
          <EditingState
            columnExtensions={EDITING_COLUMN_EXTENSIONS}
            addedRows={addedRows}
            onAddedRowsChange={(nextAddedRows) => {
              setEditingCells([]);
              setAddedRows(nextAddedRows.length ? nextAddedRows.slice(-1) : [{}]);
            }}
            editingCells={editingCells}
            onEditingCellsChange={(cells) => {
              if (!popupRow) setEditingCells(cells);
            }}
            onCommitChanges={commitChanges}
          />

          <SortingState
            columnExtensions={SORTING_STATE_COLUMN_EXTENSIONS}
            sorting={sorting}
            onSortingChange={(nextSorting) => {
              setSorting(nextSorting);
              setCurrentPage(0);
              setSelection([]);
            }}
          />

          <IntegratedSorting columnExtensions={SORTING_COLUMN_EXTENSIONS} />
          <SearchState value={searchValue} onValueChange={setSearchValue} />
          <SelectionState selection={selection} onSelectionChange={setSelection} />
          <IntegratedSelection />
          <DragDropProvider />
          <PagingState
            currentPage={currentPage}
            pageSize={pageSize}
            onCurrentPageChange={(page) => {
              setCurrentPage(page);
              setSelection([]);
            }}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(0);
              setSelection([]);
            }}
          />
          <CustomPaging totalCount={totalCount} />

          <BoxshallProductProvider for={['boxshallName']} loadOptions={loadProductOptions} />
          <ReferenceProvider for={['supplierName']} options={options.suppliers} />
          <ReferenceProvider for={['category']} options={options.categories} />
          <ReferenceProvider
            for={['subCategory']}
            options={options.subcategories}
            optionFilter={(option, row) => {
              const categoryId = row?.category?.value || row?.boxshallName?.category?.value;
              return !categoryId || String(option.category?.value) === String(categoryId);
            }}
          />
          <ReferenceProvider for={['customerRef']} options={[]} />
          <ReferenceProvider for={['class']} options={options.classes} />
          <DateProvider for={['invoiceDate']} />
          <NumberProvider for={['qty']} />
          <NumberProvider for={['unitPrice']} currency />
          <DataTypeProvider for={['invoiceFile']} formatterComponent={InvoiceFileFormatter} />

          <Table
            containerComponent={ResponsiveTableContainer}
            headComponent={StickyTableHead}
            rowComponent={(props) => (
              <ClickablePurchaseRow
                {...props}
                onOpen={(row) => {
                  setEditingCells([]);
                  setPopupRow(row);
                }}
              />
            )}
          />
          <TableColumnResizing columnWidths={completeColumnWidths} onColumnWidthsChange={setColumnWidths} />
          <TableColumnReordering order={completeColumnOrder} onOrderChange={setItemColumnOrder} />

          <TableHeaderRow showSortingControls cellComponent={HeaderFilterCell} />
          <TableSelection showSelectAll />
          <TableEditColumn
            width={85}
            showEditCommand
            showDeleteCommand
            cellComponent={(props) => (
              <KebabCommandCell
                {...props}
                onEdit={() => {
                  setEditingCells([]);
                  setPopupRow(props.row);
                }}
              />
            )}
          />
          <TableInlineCellEditing startEditAction="click" selectTextOnEditStart cellComponent={PurchaseAutoSaveCell} />
          <PagingPanel pageSizes={[25, 50, 100, 200]} />
          <TableColumnVisibility
            hiddenColumnNames={hiddenColumnNames}
            onHiddenColumnNamesChange={setHiddenColumnNames}
          />
          <Toolbar rootComponent={CustomToolbarRoot} />
          <SearchPanel />
          <ColumnChooser />
          <ExportPanel startExport={startExport} />
        </PurchaseGrid>
        <GridExporter
          ref={exporterRef}
          columns={EXPORT_COLUMNS}
          rows={rows}
          selection={selection}
          onSave={saveWorkbook}
          getRowId={(row) => row.id}
          hiddenColumnNames={hiddenColumnNames}
        />
      </Paper>

      <PurchasePopup
        open={Boolean(popupRow)}
        row={popupRow}
        options={options}
        loadProductOptions={loadProductOptions}
        saving={popupSaving}
        onSave={savePopup}
        onClose={() => setPopupRow(null)}
      />
    </Box>
  );
};

export default PurchaseTable;
